import React, { useState, useMemo, useRef, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  MenuItem,
  Select,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import AppDataGrid from "../../common/AppDataGrid";
import { collection, addDoc } from "firebase/firestore";
import { db } from "../../../config/firebase";
import { formatTime } from "../../../utils/formatDate";

const CHANGE_ACTION_OPTIONS = [
  "Cancel Course",
  "Date Change",
  "Day of Week Change",
  "Instructor Change",
  "Modality Change",
  "Multiple",
  "Proctor Change",
  "Time Change",
  "Term Change",
];

/**
 * XcsiChangeRequest
 *
 * School-facing XCSI table with inline Change Action dropdown and
 * Specific Change text field per row. A "Save Changes" button at the top
 * writes one CoursePlanningLog entry per modified row.
 *
 * Props:
 *   data          {Array}   — rows from useFetchXCSI (have _rowId stamped)
 *   loading       {boolean}
 *   bldg          {string}  — pre-filters to this Bldg code
 *   schoolId      {string}
 *   schoolName    {string}
 *   currentUser   {object}
 */
const XcsiChangeRequest = ({
  data = [],
  loading = false,
  bldg = null,
  schoolId = "",
  schoolName = "",
  currentUser = null,
  onSaveSuccess,
}) => {
  // Per-row change state: { [_rowId]: { changeAction, specificChange } }
  const [changes, setChanges] = useState({});
  const [saving, setSaving] = useState(false);

  // `columns` below is memoized with an empty dep array so its object
  // identity never changes — the DataGrid pro column model otherwise gets
  // rebuilt from scratch (remounting the Select/TextField cell editors)
  // on every keystroke, since each keystroke updates `changes` and a
  // non-memoized/re-created columns array looks like a brand-new prop to
  // the grid every render. That remount race is what was dropping most
  // keystrokes in the Specific Change box. Since the renderCell closures
  // can no longer close over the live `changes` state (that would make
  // them stale), they read it through this ref instead.
  const changesRef = useRef(changes);
  useEffect(() => {
    changesRef.current = changes;
  }, [changes]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const filteredRows = useMemo(
    () =>
      bldg
        ? (data || []).filter(
            (row) => String(row.Bldg || "").trim() === String(bldg).trim(),
          )
        : data || [],
    [data, bldg],
  );

  const hasPendingChanges = Object.values(changes).some(
    (c) => c.changeAction || c.specificChange,
  );

  const handleActionChange = (rowId, value) => {
    setChanges((prev) => ({
      ...prev,
      [rowId]: { ...prev[rowId], changeAction: value },
    }));
  };

  const handleSpecificChange = (rowId, value) => {
    setChanges((prev) => ({
      ...prev,
      [rowId]: { ...prev[rowId], specificChange: value },
    }));
  };

  const handleSave = async () => {
    const rowsToSave = filteredRows.filter(
      (row) =>
        changes[row._rowId]?.changeAction ||
        changes[row._rowId]?.specificChange,
    );

    if (rowsToSave.length === 0) {
      setSnackbar({
        open: true,
        message: "No changes to save.",
        severity: "warning",
      });
      return;
    }

    setSaving(true);
    try {
      for (const row of rowsToSave) {
        const rowChanges = changes[row._rowId] || {};
        const { _rowId, ...rowDataToSave } = row;
        const logEntry = {
          type:
            rowChanges.changeAction === "Cancel Course"
              ? "Cancel Course"
              : "Change",
          schoolId: schoolId || "",
          schoolName: schoolName || "",
          userId: currentUser?.uid || "Unknown User",
          userEmail: currentUser?.email || "Unknown Email",
          timestamp: new Date().toISOString(),
          description: `Change Request: ${rowChanges.changeAction || "N/A"} for ${row.Course_Section_Nm || "N/A"}`,
          changeAction: rowChanges.changeAction || "",
          specificChange: rowChanges.specificChange || "",
          rowData: rowDataToSave,
        };
        await addDoc(collection(db, "CoursePlanningLog"), logEntry);
      }

      // Clear saved rows' changes
      setChanges((prev) => {
        const updated = { ...prev };
        rowsToSave.forEach((row) => delete updated[row._rowId]);
        return updated;
      });

      if (onSaveSuccess) onSaveSuccess();
    } catch (err) {
      console.error("Error saving change requests:", err);
      setSnackbar({
        open: true,
        message: "Error saving changes. Please try again.",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(
    () => [
      { field: "Term_Id_Cd", headerName: "Term", minWidth: 90 },
      {
        field: "Course_Section_Nm",
        headerName: "Course Section",
        minWidth: 180,
      },
      { field: "Days", headerName: "Days", minWidth: 80 },
      {
        field: "Start_Time",
        headerName: "Start Tm",
        minWidth: 100,
        valueFormatter: (value) => formatTime(value),
      },
      {
        field: "End_Time",
        headerName: "End Tm",
        minWidth: 100,
        valueFormatter: (value) => formatTime(value),
      },
      { field: "Capacity_Ct", headerName: "Cap", minWidth: 80 },
      { field: "Used", headerName: "Used", minWidth: 80 },
      { field: "Minimum_Credit_Hour_Ct", headerName: "Cred", minWidth: 80 },
      {
        field: "Printed_Comments_Ds",
        headerName: "Print Comments",
        minWidth: 200,
      },
      { field: "Instructor", headerName: "Instructor", minWidth: 160 },
      { field: "Start_Dt", headerName: "Start Date", minWidth: 110 },
      { field: "End_Dt", headerName: "End Date", minWidth: 110 },
      {
        field: "changeAction",
        headerName: "Change Action",
        minWidth: 190,
        sortable: false,
        renderCell: (params) => (
          <Select
            displayEmpty
            size="small"
            value={changesRef.current[params.row._rowId]?.changeAction || ""}
            onChange={(e) =>
              handleActionChange(params.row._rowId, e.target.value)
            }
            onKeyDown={(e) => e.stopPropagation()}
            sx={{ width: "100%", fontSize: 13 }}
          >
            <MenuItem value="">
              <em>Select action…</em>
            </MenuItem>
            {CHANGE_ACTION_OPTIONS.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </Select>
        ),
      },
      {
        field: "specificChange",
        headerName: "Specific Change",
        minWidth: 420,
        flex: 1,
        sortable: false,
        renderCell: (params) => (
          <TextField
            size="small"
            placeholder="Describe the change…"
            value={changesRef.current[params.row._rowId]?.specificChange || ""}
            onChange={(e) =>
              handleSpecificChange(params.row._rowId, e.target.value)
            }
            onKeyDown={(e) => e.stopPropagation()}
            multiline
            minRows={3}
            fullWidth
            sx={{ width: "100%", fontSize: 13, minHeight: 96 }}
          />
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <Card sx={{ width: "100%", mt: 1 }}>
      <CardContent>
        {/* Save Changes button — only visible when there are pending changes */}
        {hasPendingChanges && (
          <Box sx={{ mb: 2 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={
                saving ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SaveIcon />
                )
              }
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving…" : "Save Changes"}
            </Button>
          </Box>
        )}

        <Box sx={{ width: "100%", overflowX: "auto" }}>
          <AppDataGrid
            rows={filteredRows}
            columns={columns}
            getRowId={(row) => row._rowId}
            loading={loading}
            autoHeight
            pagination
            pageSize={25}
            pageSizeOptions={[25, 50, 100]}
            disableColumnMenu={false}
            rowHeight={104}
            sx={{
              "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 600 },
              "& .MuiDataGrid-cell": {
                alignItems: "flex-start",
                display: "flex",
                py: 1,
              },
            }}
          />
        </Box>
      </CardContent>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default XcsiChangeRequest;
